import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert, Badge, Form } from 'react-bootstrap';
import InfoTip from '../components/InfoTip';
import Select from 'react-select';
import api from '../services/api';
import { defaultSelectStyles } from '../utils/selectStyles';

/* ─── helpers ──────────────────────────────────────────────────────────────── */
const BROWN = '#6d4e36';

const DOMAIN_COLORS = {
  RH:        '#1565c0',
  Matériels: '#795548',
  Gasoil:    '#e65100',
  Achats:    '#2e7d32',
  Ventes:    '#6d4e36',
  Finance:   '#c62828',
};

const PAGE_LABELS = {
  dashboard:          'Dashboard (Paie / Engins)',
  budget_realisation: 'Budget Réalisation',
};

const domainColor = (d) => DOMAIN_COLORS[d] || '#555';

/* ─── sous-composants UI ───────────────────────────────────────────────────── */
const SectionHeader = ({ children }) => (
  <div style={{
    background: `linear-gradient(135deg, ${BROWN} 0%, #8b5e3c 100%)`,
    color: '#fff', padding: '0.55rem 1rem',
    borderRadius: '10px 10px 0 0',
    fontWeight: 600, fontSize: '0.95rem',
  }}>
    {children}
  </div>
);

const Card = ({ children, style }) => (
  <div style={{
    borderRadius: 12, border: '1px solid rgba(109,78,54,0.15)',
    boxShadow: '0 2px 10px rgba(109,78,54,0.07)',
    background: '#fff', overflow: 'hidden', marginBottom: '1.5rem',
    ...style,
  }}>
    {children}
  </div>
);

const DirBadge = ({ dir }) => (
  <span style={{
    fontSize: '0.65rem', fontWeight: 700, borderRadius: 20,
    padding: '1px 7px',
    background: dir === 'higher_is_better' ? '#e8f5e9' : '#fbe9e7',
    color:      dir === 'higher_is_better' ? '#2e7d32' : '#b71c1c',
  }}>
    {dir === 'higher_is_better' ? '↑ plus = mieux' : '↓ moins = mieux'}
  </span>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <Form.Check
    type="switch"
    checked={!!checked}
    onChange={e => onChange(e.target.checked)}
    disabled={!!disabled}
    style={{ fontSize: '1rem' }}
  />
);

const WeightInput = ({ value, onChange }) => (
  <input
    type="number" min="0" max="10" step="0.25"
    value={value ?? ''}
    onChange={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
    style={{
      width: 62, border: `1px solid ${BROWN}55`, borderRadius: 6,
      padding: '2px 6px', fontSize: '0.82rem', textAlign: 'center',
      outline: 'none', color: BROWN, fontWeight: 600,
    }}
  />
);

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  TAB 1 — CATALOGUE KPI                                                     */
/* ═══════════════════════════════════════════════════════════════════════════ */
const CatalogueTab = () => {
  const [defs, setDefs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState({});
  const [error, setError]       = useState('');
  const [local, setLocal]       = useState({});
  const [descLocal, setDescLocal] = useState({});

  useEffect(() => {
    api.get('/kpi-admin/definitions')
      .then(r => { setDefs(r.data); setLoading(false); })
      .catch(() => { setError('Erreur de chargement.'); setLoading(false); });
  }, []);

  const patch = async (code, body) => {
    setSaving(s => ({ ...s, [code]: true }));
    try {
      await api.patch(`/kpi-admin/definitions/${code}`, body);
      setDefs(prev => prev.map(d => d.code === code ? { ...d, ...body } : d));
    } catch {
      setError('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(s => ({ ...s, [code]: false }));
    }
  };

  const handleToggleGlobal = (code, val) => {
    patch(code, { is_active_global: val ? 1 : 0 });
  };

  const handleWeightSave = (code) => {
    const w = local[code];
    if (w === undefined) return;
    patch(code, { weight_default: w });
    setLocal(l => { const n = { ...l }; delete n[code]; return n; });
  };

  const grouped = defs.reduce((acc, d) => {
    const key = d.page;
    if (!acc[key]) acc[key] = [];
    acc[key].push(d);
    return acc;
  }, {});

  if (loading) return <div className="text-center py-5"><Spinner animation="border" style={{ color: BROWN }} /></div>;

  return (
    <>
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {Object.entries(grouped).map(([page, items]) => (
        <Card key={page}>
          <SectionHeader>{PAGE_LABELS[page] || page} — {items.length} KPIs</SectionHeader>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
              <thead>
                <tr style={{ background: `${BROWN}10`, color: BROWN }}>
                  <th style={th()}>Code</th>
                  <th style={th()}>Label</th>
                  <th style={th()}>Domaine</th>
                  <th style={th()}>Unité</th>
                  <th style={th()}>Sens</th>
                  <th style={{ ...th(), textAlign: 'center' }}>Actif global</th>
                  <th style={{ ...th(), textAlign: 'center' }}>Poids défaut</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d, i) => {
                  const localW  = local[d.code];
                  const isDirty = localW !== undefined;
                  return (
                    <tr key={d.code} style={{ background: i % 2 === 0 ? '#fdfcf8' : '#fff', opacity: d.is_active_global ? 1 : 0.45 }}>
                      <td style={td()}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: BROWN }}>{d.code}</span>
                      </td>
                      <td style={{ ...td(), maxWidth: 260 }}>{d.label}</td>
                      <td style={td()}>
                        <Badge style={{ background: domainColor(d.domain), fontSize: '0.7rem' }}>{d.domain}</Badge>
                      </td>
                      <td style={td()}><span style={{ color: '#888' }}>{d.unit}</span></td>
                      <td style={td()}><DirBadge dir={d.direction} /></td>
                      <td style={{ ...td(), textAlign: 'center' }}>
                        {saving[d.code]
                          ? <Spinner animation="border" size="sm" />
                          : <Toggle
                              checked={d.is_active_global}
                              onChange={v => handleToggleGlobal(d.code, v)}
                            />
                        }
                      </td>
                      <td style={{ ...td(), textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
                          <WeightInput
                            value={isDirty ? localW : d.weight_default}
                            onChange={v => setLocal(l => ({ ...l, [d.code]: v }))}
                          />
                          {isDirty && (
                            <button
                              onClick={() => handleWeightSave(d.code)}
                              disabled={saving[d.code]}
                              style={saveBtn()}
                            >
                              {saving[d.code] ? '…' : '✓'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  TAB 2 — CONFIG PAR UTILISATEUR                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */
const ROLES_LIST = ['admin', 'responsable', 'pointeur', 'responsable_budget', 'achat'];
const ROLE_COLORS = {
  admin: '#c62828', responsable: '#1565c0', pointeur: '#2e7d32',
  responsable_budget: '#e65100', achat: '#6d4e36',
};

const UserConfigTab = () => {
  const [users, setUsers]           = useState([]);
  const [usersError, setUsersError] = useState('');
  const [roleFilter, setRoleFilter] = useState(null);
  const [selUsers, setSelUsers]     = useState([]);
  const [copyFrom, setCopyFrom]     = useState(null);
  const [configs, setConfigs]       = useState([]);
  const [dirty, setDirty]           = useState({});
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState(null);

  useEffect(() => {
    api.get('/kpi-admin/users')
      .then(r => setUsers(r.data))
      .catch(e => setUsersError(`Impossible de charger les utilisateurs (${e?.response?.status || e?.message || 'erreur réseau'})`));
  }, []);

  const loadConfig = useCallback(async (login) => {
    setLoading(true);
    setDirty({});
    setCopyFrom(null);
    try {
      const r = await api.get(`/kpi-admin/users/${login}`);
      setConfigs(r.data);
    } catch {
      setMsg({ type: 'danger', text: 'Erreur de chargement.' });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleUsersChange = (opts) => {
    const selected = opts || [];
    setSelUsers(selected);
    if (selected.length > 0) loadConfig(selected[0].value);
    else { setConfigs([]); setDirty({}); setCopyFrom(null); }
  };

  const handleCopyFrom = async (opt) => {
    setCopyFrom(opt);
    if (!opt) return;
    try {
      const r = await api.get(`/kpi-admin/users/${opt.value}`);
      const newDirty = {};
      r.data.forEach(c => {
        newDirty[c.code] = {
          is_active:    c.is_active ?? 1,
          weight:       c.weight,
          chantier_ids: c.chantier_ids,
        };
      });
      setDirty(newDirty);
      setMsg({ type: 'info', text: `Config copiée depuis ${opt.label} — vérifiez puis sauvegardez.` });
    } catch {
      setMsg({ type: 'danger', text: 'Erreur lors de la copie.' });
    }
  };

  const update = (code, field, value) => {
    setDirty(d => ({ ...d, [code]: { ...(d[code] || {}), [field]: value } }));
  };

  const save = async () => {
    if (!selUsers.length || !Object.keys(dirty).length) return;
    setSaving(true);
    setMsg(null);
    try {
      const payload = configs.map(c => {
        const changes = dirty[c.code] || {};
        return {
          kpi_code:     c.code,
          is_active:    changes.is_active    !== undefined ? changes.is_active    : (c.is_active ?? 1),
          weight:       changes.weight       !== undefined ? changes.weight       : c.weight,
          chantier_ids: changes.chantier_ids !== undefined ? changes.chantier_ids : c.chantier_ids,
        };
      });
      await Promise.all(selUsers.map(u => api.put(`/kpi-admin/users/${u.value}`, { configs: payload })));
      await loadConfig(selUsers[0].value);
      const n = selUsers.length;
      setMsg({ type: 'success', text: `✅ Config sauvegardée pour ${n} utilisateur${n > 1 ? 's' : ''}.` });
    } catch {
      setMsg({ type: 'danger', text: 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = roleFilter ? users.filter(u => u.role === roleFilter) : users;
  const userOpts      = filteredUsers.map(u => ({ value: u.login, label: `${u.name} (${u.login})`, role: u.role }));
  const copyFromOpts  = users
    .filter(u => !selUsers.find(s => s.value === u.login))
    .map(u => ({ value: u.login, label: `${u.name} (${u.login}) — ${u.role}` }));
  const hasDirty = Object.keys(dirty).length > 0;

  const grouped = configs.reduce((acc, c) => {
    if (!acc[c.page]) acc[c.page] = [];
    acc[c.page].push(c);
    return acc;
  }, {});

  return (
    <>
      <Card>
        <SectionHeader>Sélectionner un ou plusieurs utilisateurs</SectionHeader>
        <div style={{ padding: '1rem' }}>
          {usersError && <Alert variant="danger" className="mb-2">{usersError}</Alert>}

          {/* Filtre par rôle */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            <button onClick={() => setRoleFilter(null)} style={roleBtn(roleFilter === null, BROWN)}>
              Tous ({users.length})
            </button>
            {ROLES_LIST.filter(r => users.some(u => u.role === r)).map(r => (
              <button key={r} onClick={() => setRoleFilter(roleFilter === r ? null : r)}
                style={roleBtn(roleFilter === r, ROLE_COLORS[r] || BROWN)}>
                {r} ({users.filter(u => u.role === r).length})
              </button>
            ))}
          </div>

          <Select
            options={userOpts}
            value={selUsers}
            onChange={handleUsersChange}
            placeholder={users.length === 0 && !usersError ? 'Chargement…' : 'Choisir un ou plusieurs utilisateurs...'}
            isMulti
            isClearable
            menuPortalTarget={document.body}
            styles={{ ...defaultSelectStyles, control: b => ({ ...defaultSelectStyles.control(b), minHeight: 38 }) }}
          />
          {selUsers.length > 1 && (
            <div style={{ marginTop: 6, padding: '4px 10px', background: '#e3f2fd', borderRadius: 6, fontSize: '0.8rem', color: '#1565c0' }}>
              ℹ️ Config affichée : <strong>{selUsers[0].label}</strong> — la sauvegarde s'appliquera aux {selUsers.length} utilisateurs sélectionnés.
            </div>
          )}
          {users.length === 0 && !usersError && (
            <small style={{ color: '#aaa', marginTop: 4, display: 'block' }}>Aucun utilisateur trouvé.</small>
          )}
        </div>
      </Card>

      {/* Copier depuis */}
      {selUsers.length > 0 && configs.length > 0 && (
        <Card>
          <SectionHeader>Copier la config depuis un autre utilisateur</SectionHeader>
          <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <Select
                options={copyFromOpts}
                value={copyFrom}
                onChange={handleCopyFrom}
                placeholder="Choisir un utilisateur source..."
                isClearable
                menuPortalTarget={document.body}
                styles={{ ...defaultSelectStyles, control: b => ({ ...defaultSelectStyles.control(b), minHeight: 38 }) }}
              />
            </div>
            {copyFrom && (
              <span style={{ fontSize: '0.78rem', color: '#e65100', fontWeight: 600 }}>
                ⚠️ Non sauvegardé — modifiez si besoin puis cliquez Sauvegarder
              </span>
            )}
          </div>
        </Card>
      )}

      {msg && (
        <Alert variant={msg.type} dismissible onClose={() => setMsg(null)}>{msg.text}</Alert>
      )}

      {loading && <div className="text-center py-4"><Spinner animation="border" style={{ color: BROWN }} /></div>}

      {selUsers.length > 0 && !loading && configs.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button
              onClick={save}
              disabled={!hasDirty || saving}
              style={{
                background: hasDirty ? `linear-gradient(135deg, ${BROWN}, #8b5e3c)` : '#ccc',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '8px 22px', fontWeight: 700, fontSize: '0.88rem',
                cursor: hasDirty ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
            >
              {saving
                ? '⏳ Sauvegarde…'
                : `💾 Sauvegarder${hasDirty
                    ? ` (${Object.keys(dirty).length} KPI${selUsers.length > 1 ? `, ${selUsers.length} users` : ''})`
                    : ''}`
              }
            </button>
          </div>

          {Object.entries(grouped).map(([page, items]) => (
            <Card key={page}>
              <SectionHeader>{PAGE_LABELS[page] || page}</SectionHeader>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                  <thead>
                    <tr style={{ background: `${BROWN}10`, color: BROWN }}>
                      <th style={th()}>Code</th>
                      <th style={th()}>Label</th>
                      <th style={th()}>Domaine</th>
                      <th style={{ ...th(), textAlign: 'center' }}>Global</th>
                      <th style={{ ...th(), textAlign: 'center' }}>Actif pour cet user</th>
                      <th style={{ ...th(), textAlign: 'center' }}>Poids perso</th>
                      <th style={th()}>Chantiers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c, i) => {
                      const d          = dirty[c.code] || {};
                      const isAct      = d.is_active !== undefined ? d.is_active : (c.is_active ?? 1);
                      const wgt        = d.weight    !== undefined ? d.weight    : c.weight;
                      const isDirtyRow = !!dirty[c.code];
                      const globalOff  = !c.is_active_global;

                      return (
                        <tr key={c.code} style={{
                          background: isDirtyRow ? '#fffde7' : i % 2 === 0 ? '#fdfcf8' : '#fff',
                          opacity: globalOff ? 0.4 : isAct ? 1 : 0.55,
                        }}>
                          <td style={td()}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: BROWN }}>{c.code}</span>
                          </td>
                          <td style={{ ...td(), maxWidth: 220 }}>{c.label}</td>
                          <td style={td()}>
                            <Badge style={{ background: domainColor(c.domain), fontSize: '0.68rem' }}>{c.domain}</Badge>
                          </td>
                          <td style={{ ...td(), textAlign: 'center' }}>
                            {globalOff
                              ? <span style={{ fontSize: '0.68rem', color: '#b71c1c', fontWeight: 700 }}>INACTIF</span>
                              : <span style={{ fontSize: '0.68rem', color: '#2e7d32', fontWeight: 700 }}>ACTIF</span>
                            }
                          </td>
                          <td style={{ ...td(), textAlign: 'center' }}>
                            <Toggle
                              checked={isAct}
                              disabled={globalOff}
                              onChange={v => update(c.code, 'is_active', v ? 1 : 0)}
                            />
                          </td>
                          <td style={{ ...td(), textAlign: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                              <WeightInput
                                value={wgt}
                                onChange={v => update(c.code, 'weight', v)}
                              />
                              {wgt == null && (
                                <span style={{ fontSize: '0.65rem', color: '#aaa' }}>(déf. {c.weight_default})</span>
                              )}
                            </div>
                          </td>
                          <td style={td()}>
                            <span style={{ fontSize: '0.72rem', color: '#888', fontStyle: 'italic' }}>
                              {c.chantier_ids ? `[${JSON.parse(c.chantier_ids || '[]').join(', ')}]` : 'Tous'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </>
      )}
    </>
  );
};

/* ─── style helpers ─────────────────────────────────────────────────────────── */
const roleBtn = (active, color) => ({
  padding: '3px 12px', borderRadius: 20, border: `1px solid ${color}44`,
  background: active ? color : `${color}15`,
  color: active ? '#fff' : color,
  fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
  transition: 'all 0.15s',
});
const th = () => ({
  padding: '7px 10px', fontWeight: 600, fontSize: '0.75rem',
  borderBottom: '1px solid #e8dfd6', whiteSpace: 'nowrap',
});
const td = () => ({
  padding: '6px 10px', borderBottom: '1px solid #f3ede7', verticalAlign: 'middle',
});
const saveBtn = () => ({
  background: '#2e7d32', color: '#fff', border: 'none',
  borderRadius: 5, width: 26, height: 24, fontSize: '0.78rem',
  cursor: 'pointer', fontWeight: 700, lineHeight: '22px', textAlign: 'center',
});

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  PAGE PRINCIPALE                                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */
const TABS = [
  { key: 'catalogue', label: '📋 Catalogue KPI' },
  { key: 'user',      label: '👤 Config par utilisateur' },
];

export default function KpiAdminPage() {
  const [tab, setTab] = useState('catalogue');

  return (
    <div className="container-fluid mt-4" style={{ maxWidth: 1200 }}>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h4 style={{ color: BROWN, fontWeight: 700, margin: 0 }}>
          Gestion des KPI Performance
        </h4>
      </div>

      {/* ── tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '7px 18px', borderRadius: 8, fontWeight: 600,
              fontSize: '0.85rem', cursor: 'pointer', border: 'none',
              background: tab === t.key
                ? `linear-gradient(135deg, ${BROWN}, #8b5e3c)`
                : '#f0e8df',
              color: tab === t.key ? '#fff' : BROWN,
              boxShadow: tab === t.key ? '0 2px 8px rgba(109,78,54,0.25)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'catalogue' && <CatalogueTab />}
      {tab === 'user'      && <UserConfigTab />}
    </div>
  );
}
